import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Grid, Card, CardActionArea, CardContent, Stack, Chip,
    List, ListItemButton, ListItemText, Divider, Button, CircularProgress, Alert,
    IconButton, Tooltip,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../services/api';
import { dataLocalISO, emAndamento as sessaoEmAndamento } from '../services/datasLocais';

/**
 * Painel do dia (achado L2): o que precisa de ação hoje, com cada indicador
 * abrindo a lista correspondente.
 *
 * Usa apenas estados que existem no sistema:
 * - próximos atendimentos de hoje: sessões AGENDADA com horário ainda por vir;
 * - em andamento: começou e ainda está dentro da duração prevista — não é atraso;
 * - evoluções a concluir: consulta do backend (`/api/sessoes/pendentes-evolucao`),
 *   que exige horário previsto encerrado, sessão ainda AGENDADA e registro não
 *   finalizado; FALTA e CANCELADA ficam fora, e não há limite de idade;
 * - horários livres de hoje: slots com `disponivel = true`;
 * - renovações pendentes: endpoint já existente de renovação de assinatura.
 *
 * Datas usam o fuso local do consultório (`services/datasLocais`), não UTC.
 *
 * "Pendência de confirmação de presença" **não** é mostrada: não existe campo de
 * confirmação no modelo de sessão, e inventar um estado daria um número sem
 * lastro. Fica registrado como pendência de produto no checklist da auditoria.
 */

const STATUS_AGENDADA = 'AGENDADA';

const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—');
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function PainelDoDia() {
    const navigate = useNavigate();
    const [carregando, setCarregando] = useState(true);
    const [avisos, setAvisos] = useState([]);
    const [sessoesHoje, setSessoesHoje] = useState([]);
    const [pendentesEvolucao, setPendentesEvolucao] = useState([]);
    const [totalPendentes, setTotalPendentes] = useState(null);
    const [slots, setSlots] = useState([]);
    const [renovacoes, setRenovacoes] = useState([]);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setAvisos([]);
        const dia = dataLocalISO();
        const cfg = { skipGlobalError: true };
        const falhas = [];

        const [rHoje, rPendentes, rSlots, rRen] = await Promise.allSettled([
            api.get('/sessoes/periodo', { params: { inicio: `${dia}T00:00:00`, fim: `${dia}T23:59:59` }, ...cfg }),
            // Pendências de evolução vêm do backend, com a regra completa (horário
            // previsto encerrado, sessão ainda AGENDADA, sem registro vigente) e
            // SEM limite de idade — antes só os últimos 7 dias apareciam.
            api.get('/sessoes/pendentes-evolucao', { params: { page: 0, size: 6 }, ...cfg }),
            api.get('/agenda/slots', { params: { inicio: dia, fim: dia }, ...cfg }),
            api.get('/renovacao-assinatura/pendentes', cfg),
        ]);

        const lista = (r) => {
            if (r.status !== 'fulfilled') return null;
            const d = r.value.data;
            return Array.isArray(d) ? d : (d?.content ?? []);
        };

        const hoje = lista(rHoje);
        if (hoje) setSessoesHoje(hoje); else falhas.push('atendimentos de hoje');

        if (rPendentes.status === 'fulfilled') {
            const d = rPendentes.value.data;
            setPendentesEvolucao(Array.isArray(d?.content) ? d.content : []);
            // Contagem = total da MESMA consulta que gerou a lista.
            setTotalPendentes(Number(d?.totalElements ?? 0));
        } else {
            falhas.push('evoluções pendentes');
            setTotalPendentes(null);
        }

        const s = lista(rSlots);
        if (s) setSlots(s); else falhas.push('horários da agenda');

        const ren = lista(rRen);
        if (ren) setRenovacoes(ren); else falhas.push('renovações');

        setAvisos(falhas);
        setCarregando(false);
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const agora = new Date();

    // Em andamento: começou mas ainda está dentro da duração prevista. Não é
    // atraso — antes qualquer sessão cujo início já passou virava "pendente".
    const emAndamento = sessoesHoje.filter(
        (s) => s.statusSessao === STATUS_AGENDADA && sessaoEmAndamento(s, agora));

    const proximos = sessoesHoje
        .filter((s) => s.statusSessao === STATUS_AGENDADA && s.dataHora && new Date(s.dataHora) >= agora)
        .sort((a, b) => new Date(a.dataHora) - new Date(b.dataHora));

    // Pendências vêm prontas do backend (FALTA, CANCELADA e sessões em andamento
    // já ficam de fora na consulta).
    const evolucoesAConcluir = pendentesEvolucao;

    const horariosLivres = slots.filter((s) => s.disponivel);

    if (carregando) {
        return (
            <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Paper>
        );
    }

    // O titulo e a data do dia ja sao o cabecalho da pagina (Dashboard); repetir
    // "Hoje — terca-feira, 03 de novembro" aqui era a mesma informacao duas vezes
    // em quatro linhas. Fica so a acao de atualizar, e a secao se nomeia pelo
    // aria-label para o leitor de tela nao perder o contexto.
    return (
        <Box component="section" aria-label="Resumo do dia" sx={{ mb: 3 }}>
            <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: -1, mb: 0.5 }}>
                <Tooltip title="Atualizar o painel">
                    <IconButton onClick={carregar} aria-label="Atualizar o painel do dia" size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            {avisos.length > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={carregar}>Tentar de novo</Button>}
                >
                    Não foi possível carregar: {avisos.join(', ')}. Os números abaixo podem estar incompletos.
                </Alert>
            )}

            {/* Grid do MUI v7 usa `gap` real (sem margem negativa), entao o 24px
                deste flex column e exatamente o espaco entre o grupo de
                indicadores e os dois paineis. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing="20px">
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Próximos atendimentos"
                        valor={proximos.length}
                        detalhe={emAndamento.length > 0
                            ? `em andamento agora: ${hora(emAndamento[0].dataHora)}`
                            : (proximos[0] ? `Próximo às ${hora(proximos[0].dataHora)}` : 'nada mais para hoje')}
                        cor="#0277bd"
                        icone={<EventAvailableIcon sx={{ fontSize: 32, color: '#0277bd' }} />}
                        onClick={() => navigate('/agendamentos/calendario')}
                        ariaLabel="Ver agenda do dia"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Evoluções a concluir"
                        valor={totalPendentes == null ? '—' : totalPendentes}
                        detalhe={totalPendentes == null
                            ? 'não foi possível carregar'
                            : 'encerrados sem registro finalizado'}
                        cor={totalPendentes > 0 ? '#ef6c00' : '#2e7d32'}
                        icone={<PendingActionsIcon sx={{ fontSize: 32, color: totalPendentes > 0 ? '#ef6c00' : '#2e7d32' }} />}
                        onClick={() => navigate('/sessoes?status=AGENDADA')}
                        ariaLabel="Ver atendimentos com evolução pendente"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Horários livres hoje"
                        valor={horariosLivres.length}
                        detalhe={horariosLivres.length > 0 ? `a partir de ${horariosLivres[0]?.horaInicio ?? '—'}` : 'agenda cheia'}
                        cor="#00695c"
                        icone={<ScheduleIcon sx={{ fontSize: 32, color: '#00695c' }} />}
                        onClick={() => navigate('/agendamentos/calendario')}
                        ariaLabel="Ver horários livres na agenda"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Renovações pendentes"
                        valor={renovacoes.length}
                        detalhe="assinaturas aguardando renovação"
                        cor={renovacoes.length > 0 ? '#ef6c00' : '#2e7d32'}
                        icone={<AutorenewIcon sx={{ fontSize: 32, color: renovacoes.length > 0 ? '#ef6c00' : '#2e7d32' }} />}
                        onClick={() => navigate('/assinaturas-paciente')}
                        ariaLabel="Ver assinaturas com renovação pendente"
                    />
                </Grid>
            </Grid>

            {/* alignItems flex-start: com height 100% nos dois, a "Agenda de hoje"
                vazia era esticada ate a altura da lista de evolucoes e virava um
                retangulo em branco. Cada painel passa a ter a altura do que tem. */}
            <Grid container spacing="20px" sx={{ alignItems: 'flex-start' }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Agenda de hoje
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {proximos.length === 0 && emAndamento.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                Nenhum atendimento restante hoje.
                            </Typography>
                        )}
                        <List disablePadding>
                            {/* Em andamento primeiro: é o atendimento acontecendo agora. */}
                            {[...emAndamento, ...proximos].slice(0, 6).map((s) => (
                                <ListItemButton
                                    key={s.id}
                                    onClick={() => navigate(`/sessoes/${s.id}/evolucao`)}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemText
                                        primary={`${hora(s.dataHora)} · ${s.nomePaciente || 'Paciente'}`}
                                        secondary={`${s.modalidadeSessao || '—'} · ${s.duracao || '—'} min`}
                                    />
                                    <Chip
                                        size="small"
                                        label={emAndamento.some(e => e.id === s.id) ? 'EM ANDAMENTO' : s.statusSessao}
                                        color={emAndamento.some(e => e.id === s.id) ? 'info' : 'default'}
                                        sx={{ fontWeight: 600 }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Evoluções a concluir
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {evolucoesAConcluir.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                Nenhuma evolução pendente.
                            </Typography>
                        )}
                        {totalPendentes != null && totalPendentes > evolucoesAConcluir.length && (
                            <Typography variant="caption" color="text.secondary">
                                Mostrando {evolucoesAConcluir.length} de {totalPendentes}.
                            </Typography>
                        )}
                        <List disablePadding>
                            {evolucoesAConcluir.slice(0, 6).map((s) => (
                                <ListItemButton
                                    key={s.id}
                                    onClick={() => navigate(`/sessoes/${s.id}/evolucao`)}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemText
                                        primary={`${s.nomePaciente || 'Paciente'} — ${dataHora(s.dataHora)}`}
                                        secondary={s.temEvolucao ? 'registro iniciado e não finalizado' : 'sem evolução registrada'}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
            </Box>
        </Box>
    );
}

/**
 * Indicador do painel.
 *
 * Os quatro ficam lado a lado, entao as tres linhas (numero, rotulo, detalhe)
 * precisam cair na mesma altura em todos. O detalhe tem altura reservada de
 * duas linhas e e cortado com reticencias: antes, um detalhe mais longo que os
 * outros empurrava o card e o conjunto saia desalinhado. O icone ancora no topo
 * pela mesma razao -- centralizado, ele subia e descia conforme o texto.
 */
function Indicador({ titulo, valor, detalhe, cor, icone, onClick, ariaLabel }) {
    return (
        <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardActionArea onClick={onClick} aria-label={ariaLabel || titulo} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ height: '100%' }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', mt: '2px', flexShrink: 0 }}>{icone}</Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: cor, lineHeight: 1.2 }}>
                                {valor}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{titulo}</Typography>
                            <Tooltip title={detalhe}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                        minHeight: '2.4em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {detalhe}
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}
